import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import {
  Button,
  Grid,
  IconButton,
  Card,
  Select,
  MenuItem,
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { format, lastDayOfMonth } from "date-fns";
import { useStyles } from "./calendar.styles";

import {
  daysMonth,
  today,
  todayMonthIndex,
  months,
  weekDays,
  CalendarProps,
} from "./calendar.constants";
import {
  fillEmptyArray,
  getDateBy,
  getFirstAndLastDateOfMonth,
  getMonthIndexAndName,
} from "./calendar.tools";

const Calendar = ({
  selected: selectedProp,
  initialMonth,
  onApply,
  disablePast = true,
  disableWeekend = true,
  multiWeekSelection = true,
  isContainerMoved = true,
  wrapperRef,
  selectRef,
}: CalendarProps) => {
  const classes = useStyles();
  const [currentDate, setCurrentDate] = useState<Date>(today);
  const [calendarDays, setCalendarDays] = useState<daysMonth[]>();
  const [selected, setSelected] = useState<daysMonth[]>();

  const [currentMonthI, currentMonthName] = getMonthIndexAndName(currentDate);

  const { firstDate: firstDateMonth, lastDate: lastDateMonth } =
    getFirstAndLastDateOfMonth(currentDate);

  const fDayWeek = format(firstDateMonth, "iiiiii");
  const lDayWeek = format(lastDateMonth, "iiiiii");

  const isDateValid = useCallback(
    (date: Date, day: string) => {
      const isPast = disablePast
        ? date < new Date(format(today, "yyyy-MM-d"))
        : false;

      const isWeekendDisabled = disableWeekend
        ? ["Sa", "Su"].includes(day)
        : false;

      return {
        isPast,
        isWeekendDisabled,
        isDisabled: isPast || isWeekendDisabled,
      };
    },
    [disablePast, disableWeekend]
  );

  useEffect(() => {
    let dateTo = null;
    const firstSelected = selectedProp?.at(0);
    const firstSplited = firstSelected ? firstSelected.split("/") : [];

    if (firstSelected && firstSplited.length > 1) {
      dateTo = new Date(firstSelected);
    } else if (initialMonth) {
      const selectedMonth = Object.values(months).find(
        (m) => m.name === (initialMonth || format(today, "MMMM"))
      );
      dateTo = new Date(
        `${today.getFullYear()}/${selectedMonth.index}/${today.getDate()}`
      );
    } else dateTo = today;

    setCurrentDate(dateTo);
  }, [initialMonth, selectedProp]);

  useEffect(() => {
    const arrayMonth: daysMonth[] = Array(
      Number(format(lastDayOfMonth(currentDate), "d"))
    )
      .fill({ isSelected: false, isWeekSelected: false })
      .map((filledMonth, i) => {
        const daynum = i + 1;
        const cMonthDate = getDateBy(currentDate, daynum);
        const [day, week] = format(cMonthDate, "EEEEEE w").split(" ");
        const { isDisabled, isPast } = isDateValid(cMonthDate, day);

        const isCurrent =
          format(cMonthDate, "yyyy-MM-d") === format(today, "yyyy-MM-d");

        const isSelected = selectedProp
          ? selectedProp.includes(format(cMonthDate, "MM/dd/yyyy"))
          : false;

        return {
          ...filledMonth,
          number: daynum,
          date: cMonthDate,
          week,
          day,
          isCurrent,
          isPast,
          isDisabled,
          isSelected,
        };
      });

    if (weekDays[fDayWeek].index > 1)
      arrayMonth.unshift(
        ...fillEmptyArray(weekDays[fDayWeek].index - 1, arrayMonth)
      );

    if (weekDays[lDayWeek].index > 1)
      arrayMonth.push(
        ...fillEmptyArray(7 - weekDays[lDayWeek].index, arrayMonth)
      );

    const arraySelected = arrayMonth.filter((x) => x.isSelected);
    if (arraySelected.length > 1) setSelected(arraySelected);
    setCalendarDays(arrayMonth);
  }, [
    currentDate,
    disablePast,
    disableWeekend,
    fDayWeek,
    selectedProp,
    isDateValid,
    lDayWeek,
  ]);

  const handleMonthChangeClick = (index: number) => {
    const nextMonthIndex = Number(currentMonthI) + index;
    if (disablePast) {
      if (nextMonthIndex < Number(currentDate.getMonth())) return;
    }

    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + index, 1)
    );
  };

  const clearCalendarDays = () => {
    setCalendarDays((calendar) =>
      calendar.map((d) => ({
        ...d,
        isSelected: false,
        isWeekSelected: false,
      }))
    );
  };

  const handleMultipleSelection = useCallback(
    (week, day) =>
      week.map((currentWeekDay) => {
        const weekDay = { ...currentWeekDay };
        weekDay.isSelected = false;
        weekDay.isWeekSelected = false;

        if (
          [day.day, "Fr"].includes(currentWeekDay.day) ||
          weekDay.date.toString() === lastDateMonth.toString()
        ) {
          weekDay.isSelected = true;
          weekDay.isWeekSelected = false;
        } else if (!["Sa", "Su"].includes(weekDay.day)) {
          weekDay.isSelected = false;
          if (weekDays[day.day].index < weekDays[currentWeekDay.day].index)
            weekDay.isWeekSelected = true;
        }

        return weekDay;
      }),
    [lastDateMonth]
  );

  const handleSelectDayClick = (day) => {
    clearCalendarDays();
    const findWeek = calendarDays?.filter((month) => month.week === day.week);

    const weekSelected = multiWeekSelection
      ? handleMultipleSelection(findWeek, day)
      : findWeek.map((current) => {
          const weekday = { ...current };
          weekday.isSelected = weekday.day === day.day;
          weekday.isWeekSelected = false;
          return weekday;
        });

    setSelected(weekSelected.filter((w) => w.isSelected));
    setCalendarDays((current) =>
      current.map(
        (currentDay) =>
          weekSelected.find((d) => d.number === currentDay.number) || currentDay
      )
    );
  };

  const handleApplyClick = () => {
    onApply(selected);
  };

  const handleSelectChange = (e) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), Number(e.target.value) - 1, 1)
    );
  };

  const renderMenuItems = () =>
    Object.values(months).map((month) => {
      const toReturn = (
        <MenuItem key={month.index} value={month.index}>
          {month.name}
        </MenuItem>
      );

      if (disablePast) {
        if (
          currentDate.getFullYear() === today.getFullYear() &&
          month.index >= Number(todayMonthIndex)
        )
          return toReturn;
      }

      return toReturn;
    });

  return (
    <div className={classes.relative} ref={wrapperRef}>
      <Card
        className={clsx(classes.container, {
          [classes.containerLeftAndTop]: isContainerMoved,
        })}
      >
        <Grid container>
          <Grid item container xs={12} className={classes.header}>
            <Select
              className={classes.title}
              ref={selectRef}
              value={currentMonthName}
              onChange={handleSelectChange}
            >
              <MenuItem
                className={classes.hiddenMenuItem}
                value={currentMonthName}
              >
                {currentMonthName}
              </MenuItem>
              {renderMenuItems()}
            </Select>
            <Grid item>
              <IconButton
                className={classes.controlButton}
                onClick={() => handleMonthChangeClick(-1)}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                className={classes.controlButton}
                onClick={() => handleMonthChangeClick(1)}
              >
                <ChevronRight />
              </IconButton>
            </Grid>
          </Grid>
          <Grid item container className={classes.calendar}>
            {Object.values(weekDays).map((week) => (
              <Grid item key={week.index} className={classes.weekDay}>
                {week.shortName}
              </Grid>
            ))}
          </Grid>
          <Grid item container className={classes.calendar}>
            {calendarDays?.map((day) => (
              <Grid item key={day.number}>
                <IconButton
                  onClick={() => handleSelectDayClick(day)}
                  className={clsx(classes.day, {
                    [classes.isWeekSelected]: day.isWeekSelected,
                    [classes.selected]: day.isSelected,
                    [classes.current]: day.isCurrent,
                  })}
                  disabled={day.isDisabled}
                >
                  {day.week && day.number}
                </IconButton>
              </Grid>
            ))}
          </Grid>
          <Grid item className={classes.footer}>
            <Button
              disabled={!selected}
              className={classes.applyButton}
              onClick={handleApplyClick}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Card>
    </div>
  );
};

export default Calendar;
